import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { 
  createUser, 
  getUser, 
  deleteUser, 
  updateUser, 
  getAllOrganizerEvents, 
  getUserByEmail 
} from "../../../api";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { useAuth } from "../../../context/AuthContext";
import { useNightMode } from "../../../contexts/NightModeContext";
import Background from "../../Login/Background";
import { 
  UserPlus, 
  Users, 
  UserCheck, 
  ShieldCheck, 
  Mail, 
  Phone, 
  Eye, 
  EyeOff, 
  Edit3, 
  Trash2, 
  Plus, 
  Search 
} from "lucide-react";

import PageHeader from "../../../components/common/PageHeader/PageHeader";

const userTypeToRole: Record<string, number> = {
  scanner: 4,
  "second-admin": 10,
  "event-second-admin": 8,
};

const AddSubUser = () => {
  const [modal, setModal] = useState(false);
  const toggle = () => {
    if (!modal) {
      formik.resetForm();
      setUserExists(false);
    }
    setModal(!modal);
  };

  const [users, setUsers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [eventsList, setEventsList] = useState<any[]>([]);
  const [editModal, setEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [organizerId, setOrganizerId] = useState<string>("");
  const [userExists, setUserExists] = useState<boolean>(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const { isNightMode } = useNightMode();

  const toggleEditModal = () => setEditModal((prev) => !prev);

  useEffect(() => {
    const organizerData = localStorage.getItem("organizerData");
    if (organizerData) {
      setOrganizerId(JSON.parse(organizerData).organizerId);
    }
    getUserList();
    if (modal) getOnlyOrganizerEvents();
  }, [modal]);

  const getUserList = async () => {
    setIsLoading(true);
    try {
      const organizerData = localStorage.getItem("organizerData");
      if (organizerData) {
        const organizerIdd = JSON.parse(organizerData).organizerId;
        const response = await getUser(organizerIdd);
        if (response) setUsers(response);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getOnlyOrganizerEvents = async () => {
    setEventsList([]);
    try {
      const response = await getAllOrganizerEvents();
      if (Array.isArray(response)) {
        const now = new Date();
        const futureEvents = response.filter(
          (event: any) => new Date(event.eventDate) >= now
        );
        const sortedFuture = [...futureEvents].sort(
          (a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime()
        );
        const activeEvents = sortedFuture.filter(
          (event: any) => event.eventStatus === "active" || event.eventStatus === "published"
        );
        setEventsList(activeEvents);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("role", Number(e.target.value));
    formik.setFieldValue("eventId", "");
    formik.setFieldValue("venueId", "");
  };

  const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    setIsCheckingEmail(true);
    try {
      const response = await getUserByEmail(email);
      if (response && response.userId) {
        setUserExists(true);
        formik.setFieldValue("name", response.name || "");
        formik.setFieldValue("phoneNo", response.phoneNo || "");
        formik.setFieldValue("selectedRoles", response.userRoles?.length > 0 ? response.userRoles.map(Number) : response.userRole ? [Number(response.userRole)] : []);
        formik.setFieldValue("eventId", Array.isArray(response.eventId) ? response.eventId[0] : response.eventId || "");
        toast.info("User already exists. Details pre-filled.");
      } else {
        const currentEmail = formik.values.email;
        formik.resetForm();
        formik.setFieldValue("email", currentEmail);
        setUserExists(false);
      }
    } catch {
      setUserExists(false);
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleRoleToggle = (roleValue: number) => {
    const currentRoles = [...formik.values.selectedRoles];
    const index = currentRoles.indexOf(roleValue);
    if (index > -1) {
      currentRoles.splice(index, 1);
    } else {
      currentRoles.push(roleValue);
    }
    formik.setFieldValue("selectedRoles", currentRoles);
  };

  const handleEditRoleToggle = (roleValue: number) => {
    const currentRoles = [...editFormik.values.selectedRoles];
    const index = currentRoles.indexOf(roleValue);
    if (index > -1) {
      currentRoles.splice(index, 1);
    } else {
      currentRoles.push(roleValue);
    }
    editFormik.setFieldValue("selectedRoles", currentRoles);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNo: "",
      role: "",
      selectedRoles: [] as number[],
      eventId: "",
      venueId: "",
      password: "",
      status: "active"
    },
    validationSchema: Yup.lazy(() => Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phoneNo: Yup.string().required("Phone number is required"),
      role: !userExists 
        ? Yup.number().required("Role is required")
        : Yup.mixed().notRequired(),
      selectedRoles: userExists
        ? Yup.array().min(1, "Select at least one role").required("Role is required")
        : Yup.array().notRequired(),
      password: !userExists
        ? Yup.string().min(6, "Password must be at least 6 characters long").required("Password is required")
        : Yup.string().notRequired(),
    })),
    onSubmit: async (values) => {
      try {
        if (organizerId) {
          setIsSubmitting(true);
          const payload: any = {
            organizerId: organizerId,
            name: values.name,
            email: values.email,
            phoneNo: values.phoneNo,
            password: values.password,
            status: values.status,
            userRole: userExists ? Number(values.selectedRoles[0]) : (userTypeToRole[values.role] || Number(values.role)),
            userRoles: userExists ? values.selectedRoles.map(Number) : [Number(values.role)],
            ...((userExists ? values.selectedRoles.includes(userTypeToRole["event-second-admin"]) : Number(values.role) === userTypeToRole["event-second-admin"]) && values.eventId ? { eventId: [values.eventId] } : {})
          };
          if (values.role === "event-second-admin" || String(values.role) === "8") payload.eventId = [values.eventId];
          if (values.role === "venue" || String(values.role) === "3") payload.venueId = values.venueId;

          await createUser(organizerId || "", payload);
          toast.success("Sub-user added successfully");
          setModal(false);
          getUserList();
          formik.resetForm();
          setUserExists(false);
        } else {
          toast.error("Missing organizer data");
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Failed to add sub-user");
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const editFormik = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNo: "",
      role: "",
      selectedRoles: [] as number[],
      eventId: "",
      venueId: "",
      status: "active"
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      phoneNo: Yup.string().required("Phone number is required"),
      selectedRoles: Yup.array().min(1, "Select at least one role").required("Role is required"),
      status: Yup.string().oneOf(["active", "inactive"], "Invalid status").required("Status is required")
    }),
    onSubmit: async (values) => {
      try {
        if (!selectedUser || !organizerId) return;
        setIsSubmitting(true);
        const payload: any = {
          organizerId: organizerId,
          name: values.name,
          email: values.email,
          phoneNo: values.phoneNo,
          status: values.status,
          userRole: Number(values.selectedRoles[0]),
          userRoles: values.selectedRoles.map(Number),
          ...(values.selectedRoles.includes(userTypeToRole["event-second-admin"]) && values.eventId ? { eventId: [values.eventId] } : {})
        };
        
        if (values.venueId) payload.venueId = values.venueId;

        await createUser(organizerId, payload);
        toast.success("Sub-user updated successfully");
        toggleEditModal();
        getUserList();
      } catch (error: any) {
        toast.error(error?.response?.data?.message || "Failed to update sub-user");
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const getRoleDisplay = (item: any) => {
    const roles = Array.isArray(item.userRoles) && item.userRoles.length > 0 
      ? item.userRoles 
      : [item.userRole];
    
    return roles
      .map((roleId: any) => {
        const entry = Object.entries(userTypeToRole).find(([_, value]) => value === Number(roleId));
        return entry ? entry[0].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;
      })
      .filter(Boolean)
      .join(', ') || "Scanner Boy";
  };

  const handleStatusToggle = async (userId: string, currentStatus: string) => {
    const nextStatus = currentStatus === "active" ? "inactive" : "active";
    setUsers(prev =>
      prev.map(u => u.userId === userId ? { ...u, status: nextStatus } : u)
    );
    try {
      await updateUser(userId, { status: nextStatus });
      toast.success(`User status updated to ${nextStatus}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return "SU";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const activeCount = users.filter(u => u.status === "active").length;
  const adminCount = users.filter(u => {
    const roles = Array.isArray(u.userRoles) ? u.userRoles : [u.userRole];
    return roles.includes(10) || roles.includes(8);
  }).length;

  const filteredUsers = users.filter((u: any) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const name = (u.name || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const phone = (u.phoneNo || '').toLowerCase();
    const role = getRoleDisplay(u).toLowerCase();
    return name.includes(q) || email.includes(q) || phone.includes(q) || role.includes(q);
  });

  return (
    <div className={`contact-list-container ${isNightMode ? 'text-white' : 'text-dark'}`}>
      <Background />
      <ToastContainer position="top-right" autoClose={2500} theme={isNightMode ? "dark" : "light"} />

      {/* Header Section */}
      <PageHeader
        title="Sub-User Management"
        subtitle="Create, assign roles, and manage permissions for scanners and event admins."
        icon={UserPlus}
        isLoading={isLoading}
        actions={
          <button
            type="button"
            className="subuser-btn-primary"
            onClick={toggle}
          >
            <Plus size={16} /> New Sub User
          </button>
        }
      />
      {/* Stat Cards Grid */}
      <div className="contact-stats-grid">
        <div className="subuser-stat-card">
          <div>
            <div className="subuser-stat-label">TOTAL SUB-USERS</div>
            <div className="contact-stat-value">{isLoading ? "..." : users.length} <span className="fs-14px fw-normal contact-sub-text">members</span></div>
          </div>
          <div className="subuser-stat-icon pink"><Users size={22} /></div>
        </div>

        <div className="subuser-stat-card">
          <div>
            <div className="subuser-stat-label">ACTIVE ACCOUNTS</div>
            <div className="contact-stat-value">{isLoading ? "..." : activeCount} <span className="fs-14px fw-normal contact-sub-text">active</span></div>
          </div>
          <div className="subuser-stat-icon green"><UserCheck size={22} /></div>
        </div>

        <div className="subuser-stat-card">
          <div>
            <div className="subuser-stat-label">ADMINISTRATORS</div>
            <div className="contact-stat-value">{isLoading ? "..." : adminCount} <span className="fs-14px fw-normal contact-sub-text">admins</span></div>
          </div>
          <div className="subuser-stat-icon orange"><ShieldCheck size={22} /></div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="contact-filter-card">
        <div className="position-relative w-100">
          <Search className="position-absolute start-0 top-50 translate-middle-y ms-3 contact-sub-text" size={18} />
          <input
            type="text"
            className="contact-search-input"
            placeholder="Search sub-users by name, email, role, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Sub User Cards Grid */}
      {isLoading ? (
        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
          <PulseLoader color="#EE3F7C" size={14} />
          <span className="fs-12px font-mono contact-sub-text">Loading Sub-Users...</span>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="contact-table-card text-center py-5">
          <Users size={36} className="contact-sub-text mb-2" />
          <div className="fw-bold contact-name-text fs-14px">No Sub-Users Found</div>
          <div className="contact-sub-text fs-12px mb-3">Add a sub-user to delegate event scanning or management.</div>
          <button type="button" className="subuser-btn-primary" onClick={toggle}>
            <Plus size={14} /> Add Sub User Now
          </button>
        </div>
      ) : (
        <div className="row g-3">
            {filteredUsers.map((item: any) => {
              const roleName = getRoleDisplay(item);
              const isActive = item.status === "active";

              return (
                <div key={item.userId} className="col-12 col-md-6 col-xl-4">
                  <div className="subuser-card h-100">
                    
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                      <div className="d-flex align-items-center gap-3 overflow-hidden">
                      <div className="subuser-avatar flex-shrink-0">
                        {getInitials(item.name)}
                      </div>
                      <div className="overflow-hidden">
                        <div className="fw-bold fs-13px contact-name-text text-truncate">{item.name || "Unnamed User"}</div>
                        <span className="subuser-badge mt-1 d-inline-block">
                          {roleName}
                        </span>
                      </div>
                      </div>

                    <button
                      type="button"
                      className={`subuser-badge cursor-pointer border-0 flex-shrink-0 ${isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleStatusToggle(item.userId, item.status)}
                      title="Click to toggle status"
                    >
                      {isActive ? "ACTIVE" : "INACTIVE"}
                    </button>
                  </div>

                  <div className="d-flex flex-column gap-2 py-2 border-top border-white-10 fs-12px contact-sub-text">
                    <div className="d-flex align-items-center gap-2">
                      <Mail size={14} className="text-pink flex-shrink-0" />
                      <span className="text-truncate">{item.email || "No email address"}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Phone size={14} className="text-orange flex-shrink-0" />
                      <span className="text-truncate">{item.phoneNo || "No phone number"}</span>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-end gap-2 pt-3 border-top border-white-10 mt-auto">
                    <button
                      type="button"
                      className="subuser-btn-secondary"
                        onClick={() => {
                        setSelectedUser(item);
                        editFormik.setValues({
                          name: item.name || "",
                          email: item.email || "",
                          phoneNo: item.phoneNo || "",
                          role: item.userRole || "",
                          selectedRoles: Array.isArray(item.userRoles) ? item.userRoles.map(Number) : item.userRole ? [Number(item.userRole)] : [],
                          eventId: Array.isArray(item.eventId) ? item.eventId[0] : item.eventId || "",
                          venueId: item.venueId || "",
                          status: item.status || "active"
                        });
                        toggleEditModal();
                      }}
                    >
                      <Edit3 size={13} /> Edit
                    </button>

                    <button
                      type="button"
                      className="subuser-btn-secondary text-pink"
                      onClick={async () => {
                        if (window.confirm(`Are you sure you want to delete ${item.name || 'this user'}?`)) {
                          try {
                            await deleteUser(item.userId);
                            toast.success("User deleted successfully");
                            getUserList();
                          } catch {
                            toast.error("Failed to delete user");
                          }
                        }
                      }}
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  </div>

              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* CREATE SUB-USER MODAL */}
      <Modal isOpen={modal} toggle={toggle} centered contentClassName="subuser-modal-content">
        <ModalHeader toggle={toggle} className="subuser-modal-header border-bottom">
          <UserPlus size={18} className="text-pink me-2 inline" /> Add New Sub User
        </ModalHeader>
        <ModalBody className="p-3 p-sm-4">
          <Form onSubmit={formik.handleSubmit}>
            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Email Address *</Label>
              <div className="position-relative">
                <Input
                  type="email"
                  name="email"
                  className="subuser-input"
                  placeholder="name@company.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={handleEmailBlur}
                  invalid={(formik.touched.email || formik.submitCount > 0) && !!formik.errors.email}
                />
                {isCheckingEmail && (
                  <div className="position-absolute end-0 top-50 translate-middle-y me-3">
                    <PulseLoader color="#EE3F7C" size={5} />
                  </div>
                )}
                <FormFeedback>{formik.errors.email}</FormFeedback>
              </div>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Full Name *</Label>
              <Input
                type="text"
                name="name"
                className="subuser-input"
                placeholder="e.g. Sarah Jenkins"
                value={formik.values.name}
                onChange={formik.handleChange}
                invalid={(formik.touched.name || formik.submitCount > 0) && !!formik.errors.name}
              />
              <FormFeedback>{formik.errors.name}</FormFeedback>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Phone Number *</Label>
              <Input
                type="text"
                name="phoneNo"
                className="subuser-input"
                placeholder="+1 (555) 000-0000"
                value={formik.values.phoneNo}
                onChange={formik.handleChange}
                invalid={(formik.touched.phoneNo || formik.submitCount > 0) && !!formik.errors.phoneNo}
              />
              <FormFeedback>{formik.errors.phoneNo}</FormFeedback>
            </FormGroup>

            {!userExists ? (
              <FormGroup className="mb-3">
                <Label className="subuser-stat-label">Role Assignment *</Label>
                <Input
                  type="select"
                  name="role"
                  className="subuser-input"
                  value={formik.values.role}
                  onChange={handleRoleChange}
                  invalid={(formik.touched.role || formik.submitCount > 0) && !!formik.errors.role}
                >
                  <option value="">Select a role</option>
                  <option value="4">Scanner Boy (Gate Pass Scanner)</option>
                  <option value="10">Second Admin (Full Access Assistant)</option>
                  <option value="8">Event Second Admin (Event-Specific Admin)</option>
                </Input>
                <FormFeedback>{formik.errors.role}</FormFeedback>
              </FormGroup>
            ) : (
              <FormGroup className="mb-3">
                <Label className="subuser-stat-label">Select Roles *</Label>
                <div className="d-flex flex-column gap-2 mt-2 p-3 rounded-3 border border-white-10">
                  {Object.entries(userTypeToRole).map(([label, roleId]) => (
                    <FormGroup check key={roleId} className="mb-0">
                      <Label check className="fs-12px cursor-pointer">
                        <Input
                          type="checkbox"
                          checked={formik.values.selectedRoles.includes(roleId)}
                          onChange={() => handleRoleToggle(roleId)}
                          className="me-2"
                        />
                        {label === "scanner" ? "Scanner Boy" : label.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Label>
                    </FormGroup>
                  ))}
                </div>
              </FormGroup>
            )}

            {(Number(formik.values.role) === userTypeToRole["event-second-admin"] || 
              formik.values.selectedRoles.includes(userTypeToRole["event-second-admin"])) && (
              <FormGroup className="mb-3">
                <Label className="subuser-stat-label">Assigned Event *</Label>
                <Input
                  type="select"
                  name="eventId"
                  className="subuser-input"
                  value={formik.values.eventId}
                  onChange={formik.handleChange}
                  invalid={(formik.touched.eventId || formik.submitCount > 0) && !!formik.errors.eventId}
                >
                  <option value="">Select target event</option>
                  {eventsList.map((event: any) => (
                    <option key={event.eventId} value={event.eventId}>
                      {event.eventName}
                    </option>
                  ))}
                </Input>
                <FormFeedback>{formik.errors.eventId}</FormFeedback>
              </FormGroup>
            )}

            {!userExists && (
              <FormGroup className="mb-3">
                <Label className="subuser-stat-label">Password *</Label>
                <div className="position-relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="subuser-input pe-5"
                    placeholder="Enter password (min 6 characters)"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    invalid={(formik.touched.password || formik.submitCount > 0) && !!formik.errors.password}
                  />
                  <button
                    type="button"
                    className="position-absolute end-0 top-50 translate-middle-y me-3 bg-transparent border-0 text-muted-text p-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <FormFeedback>{formik.errors.password}</FormFeedback>
                </div>
              </FormGroup>
            )}

            <ModalFooter className="subuser-modal-footer px-0 pb-0 pt-3">
              <button type="button" className="subuser-btn-secondary" onClick={toggle}>
                Cancel
              </button>
              <button type="submit" className="subuser-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <PulseLoader color="#ffffff" size={8} /> : userExists ? "Update User" : "Save Sub User"}
              </button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>

      {/* EDIT SUB-USER MODAL */}
      <Modal isOpen={editModal} toggle={toggleEditModal} centered contentClassName="subuser-modal-content">
        <ModalHeader toggle={toggleEditModal} className="subuser-modal-header border-bottom">
          <Edit3 size={18} className="text-pink me-2 inline" /> Edit Sub User Details
        </ModalHeader>
        <ModalBody className="p-3 p-sm-4">
          <Form onSubmit={editFormik.handleSubmit}>
            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Email Address (Read-only)</Label>
              <Input
                type="email"
                name="email"
                className="subuser-input opacity-75"
                value={editFormik.values.email}
                disabled
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Full Name *</Label>
              <Input
                type="text"
                name="name"
                className="subuser-input"
                value={editFormik.values.name}
                onChange={editFormik.handleChange}
                invalid={(editFormik.touched.name || editFormik.submitCount > 0) && !!editFormik.errors.name}
              />
              <FormFeedback>{editFormik.errors.name}</FormFeedback>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Phone Number *</Label>
              <Input
                type="text"
                name="phoneNo"
                className="subuser-input"
                value={editFormik.values.phoneNo}
                onChange={editFormik.handleChange}
                invalid={(editFormik.touched.phoneNo || editFormik.submitCount > 0) && !!editFormik.errors.phoneNo}
              />
              <FormFeedback>{editFormik.errors.phoneNo}</FormFeedback>
            </FormGroup>

            <FormGroup className="mb-3">
              <Label className="subuser-stat-label">Assigned Roles *</Label>
              <div className="d-flex flex-column gap-2 mt-2 p-3 rounded-3 border border-white-10">
                {Object.entries(userTypeToRole).map(([label, roleId]) => (
                  <FormGroup check key={roleId} className="mb-0">
                    <Label check className="fs-12px cursor-pointer">
                      <Input
                        type="checkbox"
                        checked={editFormik.values.selectedRoles.includes(roleId)}
                        onChange={() => handleEditRoleToggle(roleId)}
                        className="me-2"
                      />
                      {label === "scanner" ? "Scanner Boy" : label.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    </Label>
                  </FormGroup>
                ))}
              </div>
            </FormGroup>

            {editFormik.values.selectedRoles.includes(userTypeToRole["event-second-admin"]) && (
              <FormGroup className="mb-3">
                <Label className="subuser-stat-label">Assigned Event</Label>
                <Input
                  type="select"
                  name="eventId"
                  className="subuser-input"
                  value={editFormik.values.eventId}
                  onChange={editFormik.handleChange}
                >
                  <option value="">Select target event</option>
                  {eventsList.map((event: any) => (
                    <option key={event.eventId} value={event.eventId}>
                      {event.eventName}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}

            <ModalFooter className="subuser-modal-footer px-0 pb-0 pt-3">
              <button type="button" className="subuser-btn-secondary" onClick={toggleEditModal}>
                Cancel
              </button>
              <button type="submit" className="subuser-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <PulseLoader color="#ffffff" size={8} /> : "Update Sub User"}
              </button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>

    </div>
  );
};

export default AddSubUser;
